import React, {useEffect, useState} from "react";
import './App.css';
import logoInk from "./ledger/logos/quantaprice-ledger-ink.svg";

interface ConsentView {
    client_name: string;
    client_host: string;
    redirect_host: string;
    redirect_is_loopback: boolean;
    tenant: string;
    user: string;
}

/**
 * Consent for a third-party (MCP) client, shown after sign-in and before any authorization code is
 * issued. oidc-proxy only answers for the browser that signed in (qp_session cookie).
 */
const Consent: React.FC = () => {
    const requestId = new URLSearchParams(window.location.search).get("request") ?? "";
    const [view, setView] = useState<ConsentView>();
    const [error, setError] = useState<string>();
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        fetch(`/api/consent/${encodeURIComponent(requestId)}`, {credentials: "same-origin"})
            .then(res => res.ok ? res.json() : Promise.reject())
            .then(setView)
            .catch(() => setError("This authorization request has expired or is not valid. Start again from the application."));
    }, [requestId]);

    const decide = async (approve: boolean) => {
        setBusy(true);
        const res = await fetch(`/api/consent/${encodeURIComponent(requestId)}`, {
            method: "POST",
            credentials: "same-origin",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({approve}),
        });
        if (!res.ok) {
            setBusy(false);
            setError("This authorization request has expired or is not valid. Start again from the application.");
            return;
        }
        window.location.href = (await res.json()).url;
    };

    return (
        <main className="ql-auth oidcp-login-wrapper">
            <div className="oidcp-logo-wrapper">
                <img className="ql-auth-logo oidcp-logo" src={logoInk} alt="Quantaprice"/>
            </div>
            <section className="ql-auth-card oidcp-provider-selector">
                {error && <div className="ql-notice ql-notice-red">{error}</div>}
                {view && <>
                    <h1 className="ql-auth-title oidcp-provider-selector-text">Allow {view.client_name}?</h1>
                    <div className="ql-auth-body">
                        <p className="ql-auth-text">
                            <strong>{view.client_name}</strong> wants to access the <strong>{view.tenant}</strong> tenant
                            as <strong>{view.user}</strong>.
                        </p>
                        <dl className="consent-facts">
                            <dt className="ql-label">Application</dt>
                            <dd className="ql-mono">{view.client_host}</dd>
                            <dt className="ql-label">Redirects to</dt>
                            <dd className="ql-mono">{view.redirect_host}</dd>
                        </dl>
                        {view.redirect_is_loopback &&
                            <div className="ql-notice ql-notice-amber">
                                This application runs on your own computer ({view.redirect_host}). Only allow it if you
                                started this sign-in yourself just now.
                            </div>}
                        <div className="consent-actions">
                            <button type="button" className="ql-btn ql-btn-primary" disabled={busy}
                                    onClick={() => decide(true)}>Allow</button>
                            <button type="button" className="ql-btn" disabled={busy}
                                    onClick={() => decide(false)}>Deny</button>
                        </div>
                    </div>
                </>}
            </section>
        </main>
    );
};

export default Consent;
